import React, { useState, useEffect } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Divider,
    Chip,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Button,
    LinearProgress,
    Snackbar,
} from "@mui/material";
import {
    Close as CloseIcon,
    Edit as EditIcon,
    LocalShipping as ShippingIcon,
    Print as PrintIcon,
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    OpenInNew as OpenInNewIcon,
    TrendingUp as TrendingUpIcon,
    Inventory as InventoryIcon,
    ShowChart as ShowChartIcon,
    CalendarToday as CalendarIcon,
    Telegram as TelegramIcon,
    CardGiftcard,
    Payments as PaymentsIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
    Purchase,
    PurchaseItem,
    PurchasePaymentCreate,
    procurementService,
} from "../../services/procurementService";
import { formatCurrencyBDT } from "../../utils/currency";
import {
    PurchaseOrderPDFGenerator,
    DEFAULT_PURCHASE_ORDER_COLUMNS,
    DEFAULT_HEADER_FIELDS,
    ColumnConfig,
    HeaderFieldConfig,
} from "../../utils/pdfGenerator";
import { getPurchaseOrderCompanyInfo } from "../../utils/purchaseOrderPdf/companyInfo";
import PrintCustomizationDialog from "../PrintCustomizationDialog";
import { AddPurchasePaymentDialog } from "./AddPurchasePaymentDialog";
import { AddBatchExpiryDialog } from "./AddBatchExpiryDialog";
import { useAccounts } from "../../hooks/useAccounts";
import { compareByProductName } from "../../utils/procurementSort";
import { ProductThumbnail } from "../common/ProductThumbnail";

interface PurchaseOrderDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    purchase: Purchase | null;
    onEdit?: (purchase: Purchase) => void;
    onReceiveItems?: (purchase: Purchase) => void;
    onSendToTelegram?: (purchase: Purchase) => void;
    onNavigate?: (direction: "prev" | "next") => void;
    onPaymentAdded?: (updated: Purchase) => void;
    onBatchAdded?: (updated: Purchase) => void;
    // Router state to hand to "Open full page" so its Back button returns
    // to the list with filters/search/page intact.
    backLocationState?: { from: string };
    hasPrev?: boolean;
    hasNext?: boolean;
    currentIndex?: number;
    totalCount?: number;
}

const paymentStatusColor = (status?: string) =>
    status === "PAID" ? "success" : status === "PARTIAL" ? "warning" : "error";

const getStatusColor = (status: string) => {
    switch (status) {
        case "PENDING":
            return "warning";
        case "CONFIRMED":
            return "info";
        case "RECEIVED":
            return "success";
        case "PARTIAL_RECEIVED":
            return "primary";
        case "CANCELLED":
            return "error";
        case "ON_HOLD":
            return "default";
        default:
            return "default";
    }
};

const getStatusIcon = (status: string) => {
    switch (status) {
        case "PENDING":
            return <ScheduleIcon fontSize="small" />;
        case "CONFIRMED":
            return <AssignmentIcon fontSize="small" />;
        case "RECEIVED":
            return <CheckCircleIcon fontSize="small" />;
        case "PARTIAL_RECEIVED":
            return <ShippingIcon fontSize="small" />;
        default:
            return <AssignmentIcon fontSize="small" />;
    }
};

const calculateStatistics = (purchase: Purchase) => {
    const totalItems = purchase.items.length;
    const totalQuantityOrdered = purchase.items.reduce(
        (sum, item) => sum + item.quantity_ordered,
        0,
    );
    const totalQuantityReceived = purchase.items.reduce(
        (sum, item) => sum + item.quantity_received,
        0,
    );
    const fulfillmentRate =
        totalQuantityOrdered > 0
            ? (totalQuantityReceived / totalQuantityOrdered) * 100
            : 0;

    let totalPotentialRevenue = 0;
    const totalCost = Number(purchase.total_amount);
    let itemsWithPricing = 0;

    purchase.items.forEach((item) => {
        if (item.product?.selling_price) {
            totalPotentialRevenue +=
                item.quantity_ordered * Number(item.product.selling_price);
            itemsWithPricing++;
        }
    });

    const expectedProfitMargin =
        totalPotentialRevenue > 0
            ? ((totalPotentialRevenue - totalCost) / totalPotentialRevenue) *
              100
            : 0;
    const expectedProfit = totalPotentialRevenue - totalCost;
    const averageCostPerItem =
        totalQuantityOrdered > 0 ? totalCost / totalQuantityOrdered : 0;

    return {
        totalItems,
        totalQuantityOrdered,
        totalQuantityReceived,
        fulfillmentRate,
        totalPotentialRevenue,
        expectedProfitMargin,
        expectedProfit,
        averageCostPerItem,
        itemsWithPricing,
    };
};

export const PurchaseOrderDetailDrawer: React.FC<
    PurchaseOrderDetailDrawerProps
> = ({
    open,
    onClose,
    purchase,
    onEdit,
    onReceiveItems,
    onSendToTelegram,
    onNavigate,
    onPaymentAdded,
    onBatchAdded,
    backLocationState,
    hasPrev = false,
    hasNext = false,
    currentIndex,
    totalCount,
}) => {
    const navigate = useNavigate();
    const { getAccountName } = useAccounts();
    const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [pdfError, setPdfError] = useState<string>("");
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [deletingPaymentId, setDeletingPaymentId] = useState<string | null>(
        null,
    );
    // The item currently targeted by "Add Expiry" — dialog is open whenever
    // this is non-null.
    const [expiryItem, setExpiryItem] = useState<PurchaseItem | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    useEffect(() => {
        if (!open || !onNavigate) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft" && hasPrev) onNavigate("prev");
            if (e.key === "ArrowRight" && hasNext) onNavigate("next");
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onNavigate, hasPrev, hasNext]);

    const handlePrint = async (
        customizedColumns: ColumnConfig[],
        headerFields: HeaderFieldConfig[],
        includeShipping: boolean,
        showSummary: boolean,
    ) => {
        if (!purchase) return;
        try {
            setIsGeneratingPDF(true);
            setPdfError("");
            const companyInfo = await getPurchaseOrderCompanyInfo();
            const pdfGenerator = new PurchaseOrderPDFGenerator(companyInfo);
            await pdfGenerator.download(
                purchase,
                customizedColumns,
                headerFields,
                includeShipping,
                showSummary,
            );
        } catch {
            setPdfError("Failed to generate PDF");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handlePreview = async (
        customizedColumns: ColumnConfig[],
        headerFields: HeaderFieldConfig[],
        includeShipping: boolean,
        showSummary: boolean,
    ) => {
        if (!purchase) return;
        try {
            setIsGeneratingPDF(true);
            setPdfError("");
            const companyInfo = await getPurchaseOrderCompanyInfo();
            const pdfGenerator = new PurchaseOrderPDFGenerator(companyInfo);
            const pdfDataUrl = await pdfGenerator.preview(
                purchase,
                customizedColumns,
                headerFields,
                includeShipping,
                showSummary,
            );
            window.open(pdfDataUrl, "_blank");
        } catch {
            setPdfError("Failed to preview PDF");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handleAddBatchExpiry = async (data: {
        quantity: number;
        expiry_date: string;
    }) => {
        if (!purchase || !expiryItem?.item_id) return;
        await procurementService.createBatch(expiryItem.item_id, {
            quantity: data.quantity,
            expiry_date: data.expiry_date,
            unit_cost: expiryItem.unit_cost,
        });
        // Refetch so the new batch chip shows up immediately.
        const updated = await procurementService.getPurchase(
            purchase.purchase_id,
        );
        onBatchAdded?.(updated);
        setSnackbar({
            open: true,
            message: "Expiry date added.",
            severity: "success",
        });
    };

    const handleAddPayment = async (data: PurchasePaymentCreate) => {
        if (!purchase) return;
        await procurementService.createPurchasePayment(
            purchase.purchase_id,
            data,
        );
        // Refetch rather than compute locally — payment_status is derived
        // server-side and this keeps it authoritative.
        const updated = await procurementService.getPurchase(
            purchase.purchase_id,
        );
        onPaymentAdded?.(updated);
        setSnackbar({
            open: true,
            message: "Payment recorded successfully.",
            severity: "success",
        });
    };

    const handleDeletePayment = async (paymentId: string) => {
        if (!purchase) return;
        if (!window.confirm("Remove this payment record?")) return;
        setDeletingPaymentId(paymentId);
        try {
            await procurementService.deletePurchasePayment(
                purchase.purchase_id,
                paymentId,
            );
            const updated = await procurementService.getPurchase(
                purchase.purchase_id,
            );
            onPaymentAdded?.(updated);
            setSnackbar({
                open: true,
                message: "Payment removed.",
                severity: "success",
            });
        } catch {
            setSnackbar({
                open: true,
                message: "Failed to remove payment.",
                severity: "error",
            });
        } finally {
            setDeletingPaymentId(null);
        }
    };

    if (!purchase) return null;

    const stats = calculateStatistics(purchase);

    const receivedSubtotal = purchase.items.reduce(
        (sum, item) =>
            sum + Number(item.quantity_received) * Number(item.unit_cost),
        0,
    );
    // total_amount is the pure item subtotal (shipping/discount apply on
    // top, not folded in) — see netPayable below.
    const orderedSubtotal = Number(purchase.total_amount);
    const isPartiallyReceived =
        receivedSubtotal < orderedSubtotal && receivedSubtotal > 0;

    const canReceive =
        purchase.status !== "RECEIVED" && purchase.status !== "CANCELLED";

    const payments = purchase.payment_transactions || [];
    const netPayable =
        purchase.net_payable ??
        Number(purchase.total_amount) -
            Number(purchase.discount_amount) +
            Number(purchase.tax_amount) +
            Number(purchase.shipping_cost);
    const amountPaid =
        purchase.amount_paid ??
        payments.reduce((sum, p) => sum + Number(p.amount), 0);
    const amountDue = purchase.amount_due ?? Math.max(0, netPayable - amountPaid);

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", sm: 700, md: 820 },
                        maxWidth: "100%",
                        mt: "64px",
                        height: "calc(100% - 64px)",
                    },
                }}
                sx={{
                    zIndex: 1200,
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="h6" component="h2" noWrap>
                                PO #{purchase.purchase_number}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 0.5,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Chip
                                    icon={getStatusIcon(purchase.status)}
                                    label={purchase.status.replace("_", " ")}
                                    color={
                                        getStatusColor(purchase.status) as any
                                    }
                                    size="small"
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <CalendarIcon fontSize="inherit" />
                                    {new Date(
                                        purchase.purchase_date,
                                    ).toLocaleDateString()}
                                </Typography>
                                {purchase.supplier && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                    >
                                        {purchase.supplier.name}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexShrink: 0,
                            }}
                        >
                            {onNavigate && (
                                <>
                                    <Tooltip title="Previous order (←)">
                                        <span>
                                            <IconButton
                                                onClick={() =>
                                                    onNavigate("prev")
                                                }
                                                disabled={!hasPrev}
                                                size="small"
                                            >
                                                <ChevronLeftIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {currentIndex !== undefined &&
                                        totalCount !== undefined && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    minWidth: 36,
                                                    textAlign: "center",
                                                    userSelect: "none",
                                                }}
                                            >
                                                {currentIndex + 1}/{totalCount}
                                            </Typography>
                                        )}
                                    <Tooltip title="Next order (→)">
                                        <span>
                                            <IconButton
                                                onClick={() =>
                                                    onNavigate("next")
                                                }
                                                disabled={!hasNext}
                                                size="small"
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Box
                                        sx={{
                                            width: "1px",
                                            height: 24,
                                            bgcolor: "divider",
                                            mx: 0.5,
                                        }}
                                    />
                                </>
                            )}
                            <Tooltip
                                title={
                                    isGeneratingPDF
                                        ? "Generating PDF..."
                                        : "Print / Download PDF"
                                }
                            >
                                <span>
                                    <IconButton
                                        onClick={() =>
                                            setIsPrintDialogOpen(true)
                                        }
                                        color="primary"
                                        disabled={isGeneratingPDF}
                                        size="small"
                                    >
                                        {isGeneratingPDF ? (
                                            <CircularProgress size={18} />
                                        ) : (
                                            <PrintIcon />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {onSendToTelegram && (
                                <Tooltip title="Send to Telegram">
                                    <IconButton
                                        onClick={() =>
                                            onSendToTelegram(purchase)
                                        }
                                        size="small"
                                        sx={{ color: "#0088cc" }}
                                    >
                                        <TelegramIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {canReceive && onReceiveItems && (
                                <Tooltip title="Receive Items">
                                    <IconButton
                                        onClick={() => onReceiveItems(purchase)}
                                        color="primary"
                                        size="small"
                                    >
                                        <ShippingIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {onEdit && (
                                <Tooltip title="Edit Purchase Order">
                                    <IconButton
                                        onClick={() => onEdit(purchase)}
                                        color="primary"
                                        size="small"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Open full page">
                                <IconButton
                                    onClick={() =>
                                        navigate(
                                            `/procurement/purchases/${purchase.purchase_id}/view`,
                                            backLocationState
                                                ? { state: backLocationState }
                                                : undefined,
                                        )
                                    }
                                    size="small"
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Scrollable content */}
                    <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>
                        {pdfError && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                                onClose={() => setPdfError("")}
                            >
                                {pdfError}
                            </Alert>
                        )}

                        {/* Statistics */}
                        <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                        >
                            Overview
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr 1fr",
                                    sm: "1fr 1fr 1fr 1fr",
                                },
                                gap: 1.5,
                                mb: 3,
                            }}
                        >
                            <Box>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        bgcolor:
                                            stats.expectedProfitMargin > 0
                                                ? "success.light"
                                                : "error.light",
                                        color:
                                            stats.expectedProfitMargin > 0
                                                ? "success.dark"
                                                : "error.dark",
                                        border: "none",
                                        borderRadius: 2,
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                        mb={0.5}
                                    >
                                        <TrendingUpIcon fontSize="small" />
                                        <Typography
                                            variant="caption"
                                            fontWeight="medium"
                                        >
                                            Profit Margin
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {stats.expectedProfitMargin.toFixed(1)}%
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.85 }}
                                    >
                                        {formatCurrencyBDT(
                                            stats.expectedProfit,
                                        )}{" "}
                                        expected
                                    </Typography>
                                </Paper>
                            </Box>
                            <Box>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        bgcolor: "info.light",
                                        color: "info.dark",
                                        border: "none",
                                        borderRadius: 2,
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                        mb={0.5}
                                    >
                                        <CheckCircleIcon fontSize="small" />
                                        <Typography
                                            variant="caption"
                                            fontWeight="medium"
                                        >
                                            Fulfillment
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {stats.fulfillmentRate.toFixed(0)}%
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.85 }}
                                    >
                                        {stats.totalQuantityReceived}/
                                        {stats.totalQuantityOrdered} units
                                    </Typography>
                                </Paper>
                            </Box>
                            <Box>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        bgcolor: "primary.light",
                                        color: "primary.dark",
                                        border: "none",
                                        borderRadius: 2,
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                        mb={0.5}
                                    >
                                        <InventoryIcon fontSize="small" />
                                        <Typography
                                            variant="caption"
                                            fontWeight="medium"
                                        >
                                            Items
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {stats.totalItems}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.85 }}
                                    >
                                        {stats.totalQuantityOrdered} total units
                                    </Typography>
                                </Paper>
                            </Box>
                            <Box>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        bgcolor: "warning.light",
                                        color: "warning.dark",
                                        border: "none",
                                        borderRadius: 2,
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                        mb={0.5}
                                    >
                                        <ShowChartIcon fontSize="small" />
                                        <Typography
                                            variant="caption"
                                            fontWeight="medium"
                                        >
                                            Pot. Revenue
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                        sx={{ lineHeight: 1.3 }}
                                    >
                                        {formatCurrencyBDT(
                                            stats.totalPotentialRevenue,
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.85 }}
                                    >
                                        Avg{" "}
                                        {formatCurrencyBDT(
                                            stats.averageCostPerItem,
                                        )}
                                        /unit
                                    </Typography>
                                </Paper>
                            </Box>
                        </Box>

                        {/* Supplier & Delivery info */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <Box>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            mb: 1,
                                        }}
                                    >
                                        <AssignmentIcon
                                            fontSize="small"
                                            color="primary"
                                        />
                                        Supplier
                                    </Typography>
                                    <Divider sx={{ mb: 1.5 }} />
                                    {purchase.supplier ? (
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                gutterBottom
                                            >
                                                {purchase.supplier.name}
                                            </Typography>
                                            {purchase.supplier
                                                .contact_person && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Contact:{" "}
                                                    {
                                                        purchase.supplier
                                                            .contact_person
                                                    }
                                                </Typography>
                                            )}
                                            {purchase.supplier.phone && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Phone:{" "}
                                                    {purchase.supplier.phone}
                                                </Typography>
                                            )}
                                            {purchase.supplier.email && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Email:{" "}
                                                    {purchase.supplier.email}
                                                </Typography>
                                            )}
                                            {purchase.supplier.address && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    Address:{" "}
                                                    {purchase.supplier.address}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Supplier details unavailable
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>
                            <Box>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            mb: 1,
                                        }}
                                    >
                                        <ShippingIcon
                                            fontSize="small"
                                            color="primary"
                                        />
                                        Delivery &amp; Payment
                                    </Typography>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0.5,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            <strong>Expected Delivery:</strong>{" "}
                                            {purchase.expected_delivery_date
                                                ? new Date(
                                                      purchase.expected_delivery_date,
                                                  ).toLocaleDateString(
                                                      "en-US",
                                                      {
                                                          year: "numeric",
                                                          month: "long",
                                                          day: "numeric",
                                                      },
                                                  )
                                                : "Not specified"}
                                        </Typography>
                                        {purchase.received_date && (
                                            <Typography variant="body2">
                                                <strong>Received Date:</strong>{" "}
                                                {new Date(
                                                    purchase.received_date,
                                                ).toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "long",
                                                    day: "numeric",
                                                })}
                                            </Typography>
                                        )}
                                        <Typography variant="body2">
                                            <strong>Payment Terms:</strong>{" "}
                                            {purchase.payment_terms ||
                                                "Not specified"}
                                        </Typography>
                                        {purchase.reference_number && (
                                            <Typography variant="body2">
                                                <strong>Reference:</strong>{" "}
                                                {purchase.reference_number}
                                            </Typography>
                                        )}
                                        <Typography variant="body2">
                                            <strong>Shipping Cost:</strong>{" "}
                                            {formatCurrencyBDT(
                                                Number(purchase.shipping_cost),
                                            )}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>

                        {/* Payment Tracking */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                }}
                            >
                                <PaymentsIcon fontSize="small" color="primary" />
                                Payment Tracking
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                {purchase.payment_status && (
                                    <Chip
                                        label={purchase.payment_status}
                                        color={
                                            paymentStatusColor(
                                                purchase.payment_status,
                                            ) as any
                                        }
                                        size="small"
                                    />
                                )}
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setPaymentDialogOpen(true)}
                                    disabled={amountDue <= 0}
                                >
                                    Record Payment
                                </Button>
                            </Box>
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Box mb={2}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    mb={0.5}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Paid: {formatCurrencyBDT(amountPaid)}{" "}
                                        of {formatCurrencyBDT(netPayable)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color={
                                            amountDue > 0
                                                ? "error.main"
                                                : "success.main"
                                        }
                                    >
                                        {amountDue > 0
                                            ? `Due: ${formatCurrencyBDT(amountDue)}`
                                            : "Fully Paid"}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={
                                        netPayable > 0
                                            ? Math.min(
                                                  100,
                                                  (amountPaid / netPayable) *
                                                      100,
                                              )
                                            : 0
                                    }
                                    color={
                                        amountDue <= 0 ? "success" : "primary"
                                    }
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>

                            {payments.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No payments recorded yet.
                                </Typography>
                            ) : (
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow
                                                sx={{ bgcolor: "action.hover" }}
                                            >
                                                <TableCell>Date</TableCell>
                                                <TableCell>Method</TableCell>
                                                <TableCell>
                                                    Reference
                                                </TableCell>
                                                <TableCell>
                                                    Account
                                                </TableCell>
                                                <TableCell align="right">
                                                    Amount
                                                </TableCell>
                                                <TableCell align="center">
                                                    Action
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {payments.map((p) => (
                                                <TableRow key={p.payment_id}>
                                                    <TableCell>
                                                        {new Date(
                                                            p.payment_date,
                                                        ).toLocaleDateString()}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={
                                                                p.payment_method
                                                            }
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {p.reference_number ||
                                                            "—"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getAccountName(
                                                            p.account_id,
                                                        ) || "—"}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <strong>
                                                            {formatCurrencyBDT(
                                                                Number(
                                                                    p.amount,
                                                                ),
                                                            )}
                                                        </strong>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={
                                                                deletingPaymentId ===
                                                                p.payment_id
                                                            }
                                                            onClick={() =>
                                                                handleDeletePayment(
                                                                    p.payment_id,
                                                                )
                                                            }
                                                        >
                                                            {deletingPaymentId ===
                                                            p.payment_id ? (
                                                                <CircularProgress
                                                                    size={16}
                                                                />
                                                            ) : (
                                                                <DeleteIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>

                        {/* Purchase Items Table */}
                        <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                        >
                            Purchase Items
                        </Typography>
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{ mb: 3 }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "action.hover" }}>
                                        <TableCell>
                                            <strong>Product</strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Ordered</strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Received</strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Remaining</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>Unit Cost</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>Total</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...purchase.items]
                                        .sort(compareByProductName)
                                        .map((item, idx) => (
                                        <TableRow
                                            key={item.item_id || idx}
                                            sx={{
                                                "&:hover": {
                                                    bgcolor: "action.hover",
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1.5,
                                                    }}
                                                >
                                                    <ProductThumbnail
                                                        images={item.product?.images}
                                                        alt={item.product?.name || ""}
                                                    />
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                        >
                                                            {item.product?.name ||
                                                                "N/A"}
                                                        </Typography>
                                                        {item.is_gift && (
                                                            <Chip
                                                                icon={
                                                                    <CardGiftcard fontSize="small" />
                                                                }
                                                                label="GIFT"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                                {!!item.bonus_quantity && (
                                                    <Typography
                                                        variant="caption"
                                                        color="secondary.main"
                                                        display="block"
                                                    >
                                                        +{item.bonus_quantity}{" "}
                                                        bonus (free)
                                                    </Typography>
                                                )}
                                                {item.notes && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        display="block"
                                                    >
                                                        {item.notes}
                                                    </Typography>
                                                )}
                                                {item.batches?.some(
                                                    (b) => b.expiry_date,
                                                ) && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 0.5,
                                                            mt: 0.5,
                                                        }}
                                                    >
                                                        {item.batches
                                                            .filter(
                                                                (b) =>
                                                                    b.expiry_date,
                                                            )
                                                            .map((b) => (
                                                                <Chip
                                                                    key={
                                                                        b.batch_id
                                                                    }
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="info"
                                                                    label={`Exp: ${new Date(
                                                                        b.expiry_date as string,
                                                                    ).toLocaleDateString()} (${b.quantity})`}
                                                                />
                                                            ))}
                                                    </Box>
                                                )}
                                                {item.quantity_received > 0 && (
                                                    <Button
                                                        size="small"
                                                        startIcon={
                                                            <AddIcon fontSize="small" />
                                                        }
                                                        onClick={() =>
                                                            setExpiryItem(item)
                                                        }
                                                        sx={{
                                                            mt: 0.5,
                                                            textTransform:
                                                                "none",
                                                        }}
                                                    >
                                                        Add Expiry
                                                    </Button>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {item.quantity_ordered}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={
                                                        item.quantity_received
                                                    }
                                                    size="small"
                                                    color={
                                                        item.quantity_received ===
                                                        item.quantity_ordered +
                                                            (item.bonus_quantity ||
                                                                0)
                                                            ? "success"
                                                            : item.quantity_received >
                                                                0
                                                              ? "primary"
                                                              : "default"
                                                    }
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    color={
                                                        (item.quantity_remaining ||
                                                            0) > 0
                                                            ? "warning.main"
                                                            : "text.secondary"
                                                    }
                                                    fontWeight={
                                                        (item.quantity_remaining ||
                                                            0) > 0
                                                            ? "bold"
                                                            : "normal"
                                                    }
                                                >
                                                    {item.quantity_remaining ||
                                                        0}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {item.is_gift ? (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.disabled"
                                                    >
                                                        FREE
                                                    </Typography>
                                                ) : (
                                                    formatCurrencyBDT(
                                                        Number(item.unit_cost),
                                                    )
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                    color={
                                                        item.is_gift
                                                            ? "text.disabled"
                                                            : "primary"
                                                    }
                                                >
                                                    {item.is_gift
                                                        ? "FREE"
                                                        : formatCurrencyBDT(
                                                              Number(
                                                                  item.total_cost,
                                                              ),
                                                          )}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Financial Summary */}
                        <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                        >
                            Financial Summary
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Box
                                sx={{
                                    maxWidth: 380,
                                    ml: "auto",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.75,
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography variant="body2">
                                        Subtotal (Ordered):
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatCurrencyBDT(orderedSubtotal)}
                                    </Typography>
                                </Box>
                                {isPartiallyReceived && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >
                                            Subtotal (Received):
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                            fontWeight="medium"
                                        >
                                            {formatCurrencyBDT(
                                                receivedSubtotal,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {Number(purchase.discount_amount) > 0 && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Typography
                                            variant="body2"
                                            color="success.main"
                                        >
                                            Discount:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="success.main"
                                        >
                                            -
                                            {formatCurrencyBDT(
                                                Number(
                                                    purchase.discount_amount,
                                                ),
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {Number(purchase.tax_amount) > 0 && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="body2">
                                            Tax:
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatCurrencyBDT(
                                                Number(purchase.tax_amount),
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography variant="body2">
                                        Shipping:
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatCurrencyBDT(
                                            Number(purchase.shipping_cost),
                                        )}
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                    >
                                        Total (Ordered):
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                        color="primary"
                                    >
                                        {formatCurrencyBDT(netPayable)}
                                    </Typography>
                                </Box>
                                {isPartiallyReceived && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                            color="success.main"
                                        >
                                            Total (Received):
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                            color="success.main"
                                        >
                                            {formatCurrencyBDT(
                                                receivedSubtotal +
                                                    Number(
                                                        purchase.shipping_cost,
                                                    ) -
                                                    Number(
                                                        purchase.discount_amount,
                                                    ) +
                                                    Number(purchase.tax_amount),
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>

                        {/* Notes */}
                        {purchase.notes && (
                            <>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    gutterBottom
                                >
                                    Notes
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                        {purchase.notes}
                                    </Typography>
                                </Paper>
                            </>
                        )}
                    </Box>
                </Box>
            </Drawer>

            <PrintCustomizationDialog
                open={isPrintDialogOpen}
                onClose={() => setIsPrintDialogOpen(false)}
                columns={DEFAULT_PURCHASE_ORDER_COLUMNS}
                headerFields={DEFAULT_HEADER_FIELDS}
                onPrint={handlePrint}
                onPreview={handlePreview}
            />

            <AddPurchasePaymentDialog
                open={paymentDialogOpen}
                onClose={() => setPaymentDialogOpen(false)}
                onConfirm={handleAddPayment}
                amountDue={amountDue}
            />

            <AddBatchExpiryDialog
                open={!!expiryItem}
                onClose={() => setExpiryItem(null)}
                onConfirm={handleAddBatchExpiry}
                productName={expiryItem?.product?.name || "This product"}
                maxQuantity={expiryItem?.quantity_received || 0}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PurchaseOrderDetailDrawer;
